import {
  EScreenType,
  ESymbolCategory,
  IScreenDesignDto,
  IScreenDesignInfo,
  ISymbol,
  IWFParameter,
  IWFStep,
  IWorkFlow
} from "@kuika/kuika-cl-model";
import { IInteractionParameter, IUserInteraction } from "@kuika/kuika-cl-model/src/models/workflow";
import { AxiosResponse } from "axios";
import { Guid } from "guid-typescript";
import { ScreenService } from "../../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { ParameterDefaultValueInput } from "../../right-panel/workflow-panel/wf-utils/parameter-default-value-input";
import { AddButtonModal } from "./add-button-modal";
import { IButtonPreview, parseButtonPreview } from "./button-preview";
import FormsTabStepOutputs, { typeIcon } from "./forms-tab-step-outputs";
import { getStepOutputParams, getUserTaskSteps } from "./forms-tab-steps";

/** The default fixed value a freshly auto-added interaction parameter carries (per data type). */
const defaultFixedValue = (dataType: string): string | number | boolean => {
  switch (dataType) {
    case "Boolean":
      return false;
    case "DateTimeOffset":
      return new Date().toISOString();
    case "Int16":
    case "Int32":
    case "Int64":
    case "Single":
    case "Decimal":
    case "Double":
      return 0;
    default:
      return "";
  }
};

export interface IFormsTabInteractionsProps {
  /** The user-task step whose interactions are edited (selected in the shared sidebar). */
  selectedStepId: string | null;
  /**
   * Fired after each persisted interaction edit. The step's screen renders its interactions as
   * generated buttons, so the parent must queue the step for the next Apply Screens — the workflow
   * save alone never regenerates the screen.
   */
  onChanged?: (stepId: string) => void;
}

/** A selectable button component (an `EScreenType.Component` screen) in the Select Button dropdown. */
interface IButtonOption {
  value: string;
  label: JSX.Element;
  /** Plain name — the ReactNode label can't be text-matched, so search filters on this. */
  searchLabel: string;
}

export interface IFormsTabInteractionsState {
  /** Bumped after each in-place mutation to force a re-render (interactions mutate the live WF step). */
  rev: number;
  expanded: Set<string>;
  adding: boolean;
  newName: string;
  /** Id of the interaction whose button dropdown is open (controlled, so Create/Edit can close it). */
  buttonDdOpenId: string | null;
  /** Id of the interaction the Add Button modal was opened for; the created button auto-selects there. */
  buttonModalFor: string | null;
  /** The button component being edited (full dto, fetched on demand) — opens the modal in edit mode. */
  editButton: IScreenDesignDto | null;
  /** Button label + colors per component screen id, lazily fetched to render styled option pills. */
  buttonPreviews: Map<string, IButtonPreview>;
  /** The Select Button dropdown's search text (shared by the control input and the in-dropdown box). */
  buttonSearch: string;
}

/**
 * Inline editor for a step's **user interactions** (moved here from the Diagram property panel). Each
 * interaction auto-lists ALL of the step's output parameters: internal params need a mandatory fixed
 * value; Business Object params sit under an info note (only needed for bulk-approve / mail quick
 * interactions — otherwise the form fills them). The Select Button dropdown offers the project's
 * button components and can create a new one inline (via AddButtonModal; the
 * created button auto-selects). Edits mutate the live `sd.selectedWf` step (as the property panel
 * did) and persist via `sd.saveWorkFlow` on each discrete commit.
 */
class FormsTabInteractions extends PureSdComponent<IFormsTabInteractionsProps, IFormsTabInteractionsState> {
  constructor(props: IFormsTabInteractionsProps & IWithSDProps) {
    super(props);
    this.state = {
      rev: 0,
      expanded: new Set(),
      adding: false,
      newName: "",
      buttonDdOpenId: null,
      buttonModalFor: null,
      editButton: null,
      buttonPreviews: new Map(),
      buttonSearch: ""
    };
  }

  /** Preview fetches currently in flight — an instance field (not render state) to dedupe requests. */
  private previewsInFlight: Set<string> = new Set();

  /** True while the in-dropdown search input owns focus — rc-select's blur-close is ignored then. */
  private buttonSearchActive: boolean = false;

  componentDidMount(): void {
    this.normalizeStep();
    this.ensureButtonPreviews();
  }

  componentDidUpdate(prevProps: IFormsTabInteractionsProps & IWithSDProps): void {
    if (prevProps.selectedStepId !== this.props.selectedStepId) this.normalizeStep();
    // The screen list gains buttons over time (create, other panels) — keep previews caught up.
    this.ensureButtonPreviews();
  }

  private get workflow(): IWorkFlow | undefined {
    return this.props.sd.selectedWf;
  }

  private get step(): IWFStep | undefined {
    const wf: IWorkFlow | undefined = this.workflow;
    const stepId: string | null = this.props.selectedStepId;
    if (!wf || !stepId) return undefined;
    return this.props.sd.findStepRecursively(wf, (s: IWFStep): boolean => s.id === stepId);
  }

  /** The step's output parameters resolved to their workflow-parameter metadata (scalars only). */
  private outputParams = (): IWFParameter[] => getStepOutputParams(this.workflow, this.step);

  private internalOutputs = (): IWFParameter[] =>
    this.outputParams().filter((p: IWFParameter): boolean => p.isBusinessObject === false);

  private boOutputs = (): IWFParameter[] =>
    this.outputParams().filter((p: IWFParameter): boolean => p.isBusinessObject === true);

  private buildParam = (p: IWFParameter): IInteractionParameter => ({
    id: p.id,
    isMailParameter: false,
    value: {
      ...(p.defaultValue ?? {}),
      label: "Fixed Value",
      category: ESymbolCategory.fixed,
      fixedValue: defaultFixedValue(p.dataType)
    } as ISymbol
  });

  /**
   * Ensure every interaction carries an entry for every current output parameter (older interactions
   * from the modal only held internal params). In-memory only — persisted on the next user commit.
   */
  private normalizeStep = (): void => {
    const step: IWFStep | undefined = this.step;
    if (!step?.userInteractions?.length) return;
    const outs: IWFParameter[] = this.outputParams();
    let changed: boolean = false;
    for (const interaction of step.userInteractions) {
      if (!interaction.parameters) interaction.parameters = [];
      for (const p of outs) {
        if (!interaction.parameters.some((x: IInteractionParameter): boolean => x.id === p.id)) {
          interaction.parameters.push(this.buildParam(p));
          changed = true;
        }
      }
    }
    if (changed)
      this.setState((s: IFormsTabInteractionsState): Pick<IFormsTabInteractionsState, "rev"> => ({ rev: s.rev + 1 }));
  };

  /** Persist the live workflow, notify the parent (Apply eligibility), and force a re-render. */
  private commit = (): void => {
    const wf: IWorkFlow | undefined = this.workflow;
    if (wf) {
      void this.props.sd.saveWorkFlow(wf);
      const stepId: string | null = this.props.selectedStepId;
      if (stepId) this.props.onChanged?.(stepId);
    }
    this.setState((s: IFormsTabInteractionsState): Pick<IFormsTabInteractionsState, "rev"> => ({ rev: s.rev + 1 }));
  };

  private bump = (): void =>
    this.setState((s: IFormsTabInteractionsState): Pick<IFormsTabInteractionsState, "rev"> => ({ rev: s.rev + 1 }));

  private addInteractionWithName = (rawName: string): void => {
    const step: IWFStep | undefined = this.step;
    const name: string = rawName.trim();
    if (!step || !name) return;
    const interaction: IUserInteraction = {
      id: Guid.create().toString(),
      label: name,
      parameters: this.outputParams().map((p: IWFParameter): IInteractionParameter => this.buildParam(p))
    };
    if (!step.userInteractions) step.userInteractions = [];
    step.userInteractions.push(interaction);
    const expanded: Set<string> = new Set(this.state.expanded);
    expanded.add(interaction.id);
    this.setState({ adding: false, newName: "", expanded });
    this.commit();
  };

  private addInteraction = (): void => this.addInteractionWithName(this.state.newName);

  private cancelAdd = (): void => this.setState({ adding: false, newName: "" });

  /**
   * Distinct interaction names already used elsewhere in this process, offered as suggestions so the
   * user reuses the SAME name across steps — the key to grouping them for bulk approval. Names already
   * on the current step are excluded (a step can't hold the same interaction twice).
   */
  private existingNames = (): string[] => {
    const currentLabels: Set<string> = new Set(
      (this.step?.userInteractions ?? []).map((i: IUserInteraction): string => (i.label ?? "").trim())
    );
    const byLower: Map<string, string> = new Map();
    for (const s of getUserTaskSteps(this.workflow)) {
      for (const i of s.userInteractions ?? []) {
        const label: string = (i.label ?? "").trim();
        if (label && !currentLabels.has(label)) byLower.set(label.toLowerCase(), label);
      }
    }
    return [...byLower.values()].sort((a: string, b: string): number => a.localeCompare(b));
  };

  private deleteInteraction = (id: string): void => {
    const step: IWFStep | undefined = this.step;
    if (!step) return;
    step.userInteractions = (step.userInteractions ?? []).filter((i: IUserInteraction): boolean => i.id !== id);
    // A step's mail interactions reference user interactions by id — drop any dangling reference.
    if (step.mailInteractions) {
      step.mailInteractions = step.mailInteractions.filter((m): boolean => m.userInteractionId !== id);
    }
    this.commit();
  };

  private toggleExpanded = (id: string): void => {
    const expanded: Set<string> = new Set(this.state.expanded);
    if (expanded.has(id)) {
      expanded.delete(id);
    } else {
      expanded.add(id);
    }
    this.setState({ expanded });
  };

  /** The project's button components (Component-type screens), the Select Button option source. */
  private buttonScreens = (): IScreenDesignInfo[] =>
    (this.props.sd.screenList ?? []).filter(
      (s: IScreenDesignInfo): boolean => s.publicMetaData?.screenType === EScreenType.Component
    );

  /**
   * Lazily fetch the design of every button component we don't have a preview for yet, so the
   * dropdown can render each option as its actual styled pill. A failed fetch caches the name-only
   * fallback (instead of retrying every update).
   */
  private ensureButtonPreviews = (): void => {
    const missing: IScreenDesignInfo[] = this.buttonScreens().filter(
      (s: IScreenDesignInfo): boolean => !this.state.buttonPreviews.has(s.id) && !this.previewsInFlight.has(s.id)
    );
    if (missing.length === 0) return;
    for (const screen of missing) this.previewsInFlight.add(screen.id);
    void this.loadButtonPreviews(missing);
  };

  private loadButtonPreviews = async (screens: IScreenDesignInfo[]): Promise<void> => {
    const results: PromiseSettledResult<AxiosResponse<IScreenDesignDto>>[] = await Promise.allSettled(
      screens.map((s: IScreenDesignInfo): Promise<AxiosResponse<IScreenDesignDto>> => ScreenService.Get(s.id))
    );
    this.setState((prev: IFormsTabInteractionsState): Pick<IFormsTabInteractionsState, "buttonPreviews"> => {
      const buttonPreviews: Map<string, IButtonPreview> = new Map(prev.buttonPreviews);
      results.forEach((result: PromiseSettledResult<AxiosResponse<IScreenDesignDto>>, index: number): void => {
        const screen: IScreenDesignInfo = screens[index];
        this.previewsInFlight.delete(screen.id);
        if (result.status === "fulfilled" && result.value.status === 200) {
          buttonPreviews.set(screen.id, parseButtonPreview(result.value.data));
        } else {
          buttonPreviews.set(screen.id, { label: screen.name, textColor: "#ffffff", backgroundColor: "#0358ff" });
        }
      });
      return { buttonPreviews };
    });
  };

  /** Fetch the full button design fresh and open the modal in edit mode. */
  private onEditButton = async (screenId: string): Promise<void> => {
    this.setState({ buttonDdOpenId: null });
    try {
      const response: AxiosResponse<IScreenDesignDto> = await ScreenService.Get(screenId);
      if (response.status === 200) this.setState({ editButton: response.data });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  private renderButtonOptionLabel = (screen: IScreenDesignInfo): JSX.Element => {
    const preview: IButtonPreview | undefined = this.state.buttonPreviews.get(screen.id);
    return (
      <span className="kuika-wf-forms-btn-dd__option">
        {preview && (
          <span
            className="kuika-wf-forms-btn-dd__pill"
            // --btn-pill-color lets the stylesheet re-assert the pill's own text color on the
            // selected row, where the dropdown otherwise forces every span to blue.
            style={
              {
                backgroundColor: preview.backgroundColor,
                color: preview.textColor,
                "--btn-pill-color": preview.textColor
              } as React.CSSProperties
            }
          >
            {preview.label}
          </span>
        )}
        <span className="kuika-wf-forms-btn-dd__option-name">{screen.name}</span>
        <span
          className="kuika-wf-forms-btn-dd__edit"
          title="Edit this button"
          // Don't let the click select the option or steal the select's focus.
          onMouseDown={(e: React.MouseEvent<HTMLSpanElement>): void => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e: React.MouseEvent<HTMLSpanElement>): void => {
            e.stopPropagation();
            void this.onEditButton(screen.id);
          }}
          data-testid={`processFormsEditButton-${screen.id}`}
        >
          <KuikaSvg name="edit" width={13} height={13} color="var(--dark-9)" />
        </span>
      </span>
    );
  };

  private buttonOptions = (): IButtonOption[] =>
    this.buttonScreens().map((s: IScreenDesignInfo): IButtonOption => ({
      value: s.id,
      label: this.renderButtonOptionLabel(s),
      searchLabel: s.name
    }));

  /** The freshly created button component: auto-select it on the interaction the modal was opened for. */
  private handleButtonCreated = (button: IScreenDesignDto): void => {
    const step: IWFStep | undefined = this.step;
    const interactionId: string | null = this.state.buttonModalFor;
    if (!step || !interactionId) return;
    const interaction: IUserInteraction | undefined = step.userInteractions?.find(
      (i: IUserInteraction): boolean => i.id === interactionId
    );
    if (!interaction) return;
    interaction.buttonId = button.id;
    this.commit();
  };

  /** Close the Add/Edit Button modal; an edited button's preview is dropped and refetched. */
  private closeButtonModal = (): void => {
    const editedId: string | undefined = this.state.editButton?.id;
    this.setState(
      (
        prev: IFormsTabInteractionsState
      ): Pick<IFormsTabInteractionsState, "buttonModalFor" | "editButton" | "buttonPreviews"> => {
        const buttonPreviews: Map<string, IButtonPreview> = new Map(prev.buttonPreviews);
        if (editedId) buttonPreviews.delete(editedId);
        return { buttonModalFor: null, editButton: null, buttonPreviews };
      },
      (): void => this.ensureButtonPreviews()
    );
  };

  private renderButtonDdHeader = (): JSX.Element => (
    <span className="kuika-wf-forms-btn-dd__head">
      <span
        className="kuika-wf-forms-btn-dd__search"
        // The header wrapper preventDefaults mousedown to keep focus in the select — stop the
        // bubble so this input can take focus, and flag it so the select's blur doesn't close us.
        onMouseDown={(e: React.MouseEvent<HTMLSpanElement>): void => {
          e.stopPropagation();
          this.buttonSearchActive = true;
        }}
        onBlur={(): void => {
          this.buttonSearchActive = false;
        }}
      >
        <KuikaInput
          size="sm"
          placeholder="Search buttons"
          value={this.state.buttonSearch}
          leftIcon={<KuikaSvg name="search" width={13} height={13} color="var(--dark-7)" />}
          onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
            this.setState({ buttonSearch: String(value) })
          }
          data-testid="processFormsButtonSearch"
        />
      </span>
      <span className="kuika-wf-forms-btn-dd__head-title">Select button style</span>
      <span className="kuika-wf-forms-btn-dd__head-hint">
        <KuikaSvg name="info-circle" width={12} height={12} color="var(--dark-7)" />
        Leave empty to use the default style.
      </span>
    </span>
  );

  private renderButtonDdFooter = (interactionId: string): JSX.Element => (
    <button
      type="button"
      className="kuika-wf-forms-btn-dd__create"
      // Keep focus in the select so the dropdown doesn't flicker closed before onClick fires.
      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>): void => e.preventDefault()}
      onClick={(): void => this.setState({ buttonDdOpenId: null, buttonModalFor: interactionId })}
      data-testid="processFormsCreateButton"
    >
      <KuikaSvg name="plus" width={14} height={14} />
      Create a New Button
    </button>
  );

  private renderValueEditor = (interaction: IUserInteraction, p: IWFParameter) => {
    const param: IInteractionParameter | undefined = interaction.parameters?.find(
      (x: IInteractionParameter): boolean => x.id === p.id
    );
    if (!param?.value) return null;
    return (
      <ParameterDefaultValueInput
        symbol={param.value}
        valueType={p.dataType}
        borderless
        placeholder="Enter value"
        onValueChange={(value: boolean): void => {
          // Only the Boolean editor routes through onValueChange; others mutate the symbol in place.
          param.value!.fixedValue = value;
          this.commit();
        }}
      />
    );
  };

  private renderParamRow = (interaction: IUserInteraction, p: IWFParameter) => (
    <div className="kuika-wf-forms__int-row" key={p.id}>
      <span className="kuika-wf-forms__int-type">
        <KuikaSvg name={typeIcon(p.dataType)} width={14} height={14} color="var(--dark-9)" />
        {p.dataType}
      </span>
      <span className="kuika-wf-forms__int-param">{p.name}</span>
      <span className="kuika-wf-forms__int-value">{this.renderValueEditor(interaction, p)}</span>
    </div>
  );

  private renderInteraction = (interaction: IUserInteraction) => {
    const expanded: boolean = this.state.expanded.has(interaction.id);
    const internal: IWFParameter[] = this.internalOutputs();
    const bo: IWFParameter[] = this.boOutputs();
    return (
      // Card-level blur persists in-place edits (text / number / date mutate the symbol on blur).
      <div
        className="kuika-wf-forms__int-card"
        key={interaction.id}
        onBlur={(): void => this.commit()}
        data-testid={`processFormsInteractionCard-${interaction.id}`}
      >
        <div className="kuika-wf-forms__int-card-head">
          <button
            type="button"
            className="kuika-wf-forms__int-chevron"
            onClick={(): void => this.toggleExpanded(interaction.id)}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <KuikaSvg name={expanded ? "chevron-down" : "chevron-right"} type="arrows" width={14} height={14} />
          </button>
          <input
            className="kuika-wf-forms__int-name"
            value={interaction.label}
            placeholder="Interaction name"
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              interaction.label = e.target.value;
              this.bump();
            }}
            onBlur={(): void => this.commit()}
            data-testid={`processFormsInteractionLabel-${interaction.id}`}
          />
          <KuikaSvg
            name="trash"
            width={14}
            height={14}
            pointer
            color="var(--dark-9)"
            onClick={(): void => this.deleteInteraction(interaction.id)}
          />
        </div>

        {expanded && (
          <div className="kuika-wf-forms__int-card-body">
            {internal.length > 0 && (
              <div className="kuika-wf-forms__int-table">
                <div className="kuika-wf-forms__int-row kuika-wf-forms__int-row--head">
                  <span className="kuika-wf-forms__int-type">Type</span>
                  <span className="kuika-wf-forms__int-param">Parameter</span>
                  <span className="kuika-wf-forms__int-value">Value</span>
                </div>
                {internal.map((p: IWFParameter) => this.renderParamRow(interaction, p))}
              </div>
            )}

            {bo.length > 0 && (
              <div className="kuika-wf-forms__int-bo">
                <div className="kuika-wf-forms__int-bo-note">
                  <KuikaSvg name="info-circle" width={14} height={14} color="var(--tone-blue-fg)" />
                  <span>
                    If you don&apos;t set a value for{" "}
                    <strong>{bo.map((p: IWFParameter): string => p.name).join(", ")}</strong> below, this interaction
                    won&apos;t override them — they&apos;re filled from the form and can stay empty. Set them only for
                    bulk-approve and mail quick-interactions. <strong>Note:</strong> while these are empty, the
                    interaction can&apos;t be used in notifications (the &quot;User Interactions&quot; mail buttons and
                    the custom template&apos;s Mail Action Links) — a notification has no form to fill them from.
                  </span>
                </div>
                <div className="kuika-wf-forms__int-table">
                  <div className="kuika-wf-forms__int-row kuika-wf-forms__int-row--head">
                    <span className="kuika-wf-forms__int-type">Type</span>
                    <span className="kuika-wf-forms__int-param">Parameter</span>
                    <span className="kuika-wf-forms__int-value">Value</span>
                  </div>
                  {bo.map((p: IWFParameter) => this.renderParamRow(interaction, p))}
                </div>
              </div>
            )}

            <label className="kuika-wf-forms__int-button">
              <span className="kuika-wf-forms__int-button-label">
                <KuikaSvg name="hand-click" width={14} height={14} color="var(--dark-11)" />
                Select Button
                <span className="kuika-wf-forms__int-button-optional">(Optional)</span>
              </span>
              <KuikaSelectbox
                size="md"
                allowClear
                searchable
                placeholder="No button selected"
                value={interaction.buttonId ?? null}
                options={this.buttonOptions()}
                overlayClassName="kuika-wf-forms-btn-dd-overlay"
                visible={this.state.buttonDdOpenId === interaction.id}
                onVisibleChange={(visible: boolean): void => {
                  // rc-select asks to close when its control blurs — ignore that while the
                  // in-dropdown search box owns the focus, or typing would dismiss the dropdown.
                  if (!visible && this.buttonSearchActive) return;
                  this.setState({ buttonDdOpenId: visible ? interaction.id : null, buttonSearch: "" });
                }}
                searchValue={this.state.buttonSearch}
                onSearch={(value: string): void => this.setState({ buttonSearch: value })}
                dropdownHeader={this.renderButtonDdHeader()}
                dropdownFooter={this.renderButtonDdFooter(interaction.id)}
                onChange={(value: string | number | (string | number)[] | null): void => {
                  interaction.buttonId = value == null ? undefined : String(value);
                  // Close explicitly: the swallowed blur-close above must not leave us stuck open.
                  this.buttonSearchActive = false;
                  this.setState({ buttonDdOpenId: null, buttonSearch: "" });
                  this.commit();
                }}
              />
            </label>
          </div>
        )}
      </div>
    );
  };

  private renderAddRow = () => {
    const q: string = this.state.newName.trim().toLowerCase();
    const suggestions: string[] = this.existingNames().filter(
      (n: string): boolean => !q || n.toLowerCase().includes(q)
    );
    return (
      <div className="kuika-wf-forms__int-add">
        <div className="kuika-wf-forms__int-addrow">
          <KuikaInput
            size="md"
            placeholder="Search an existing name or type a new one…"
            value={this.state.newName}
            onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
              this.setState({ newName: String(value) })
            }
            onPressEnter={(): void => {
              if (this.state.newName.trim()) this.addInteraction();
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => {
              if (e.key === "Escape") {
                e.preventDefault();
                this.cancelAdd();
              }
            }}
            data-testid="processFormsInteractionName"
          />
          <KuikaButton
            type="primary"
            size="md"
            uppercase
            disabled={!this.state.newName.trim()}
            onClick={this.addInteraction}
          >
            Add
          </KuikaButton>
          <KuikaButton type="text" size="md" uppercase onClick={this.cancelAdd}>
            Cancel
          </KuikaButton>
        </div>
        {suggestions.length > 0 && (
          <div className="kuika-wf-forms__int-suggestions">
            <span className="kuika-wf-forms__int-suggestions-label">
              Used in this process — reuse a name to group steps for bulk approval
            </span>
            <div className="kuika-wf-forms__int-suggestions-list">
              {suggestions.map((n: string) => (
                <button
                  type="button"
                  key={n}
                  className="kuika-wf-forms__int-suggestion"
                  onClick={(): void => this.addInteractionWithName(n)}
                  data-testid={`processFormsInteractionSuggestion-${n}`}
                >
                  <KuikaSvg name="checks" width={12} height={12} color="var(--dark-9)" />
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  render() {
    const step: IWFStep | undefined = this.step;
    if (!step) {
      return <div className="kuika-wf-forms__int-empty">Select a user task to manage its interactions.</div>;
    }
    const outputs: IWFParameter[] = this.outputParams();
    const interactions: IUserInteraction[] = step.userInteractions ?? [];

    return (
      <div className="kuika-wf-forms__int" data-testid="processFormsInteractions">
        <FormsTabStepOutputs params={outputs} />

        <div className="kuika-wf-forms__int-head">
          <span className="kuika-wf-forms__int-head-title">
            User Interactions
            <span className="kuika-wf-forms__int-head-hint">
              Every interaction automatically lists this step&apos;s outputs — no separate mapping needed.
            </span>
          </span>
          <KuikaButton
            type="text"
            uppercase
            onClick={(): void => this.setState({ adding: true })}
            data-testid="processFormsAddInteraction"
          >
            <KuikaSvg name="plus" width={14} height={14} />
            Add New User Interaction
          </KuikaButton>
        </div>

        {this.state.adding && this.renderAddRow()}

        {interactions.map((interaction: IUserInteraction) => this.renderInteraction(interaction))}

        {interactions.length === 0 && !this.state.adding && (
          <div className="kuika-wf-forms__int-empty">
            No interactions yet. Add one to define the buttons that resume this step.
          </div>
        )}

        <AddButtonModal
          isEdit={this.state.editButton !== null}
          button={this.state.editButton}
          showModal={this.state.buttonModalFor !== null || this.state.editButton !== null}
          onClose={this.closeButtonModal}
          onCreated={this.handleButtonCreated}
        />
      </div>
    );
  }

  checkNeedRender = (): boolean => true;
}

const formsTabInteractions = withSD<IFormsTabInteractionsProps>(FormsTabInteractions);
export default formsTabInteractions;
